import { authContracts, billingApi, ErrorResponse, extractMethodFromKey, extractPathFromKey, extractPathParams, publicContracts, studentContracts, substitutePath, teacherContracts, } from '@studoku/shared';
import * as v from 'valibot';
import { SCHEMA_EPOCH } from './contracts/epoch.js';
export function makeClient(options) {
    return client({
        ...authContracts,
        ...billingApi,
        ...publicContracts,
        ...studentContracts,
        ...teacherContracts,
    }, options);
}
// Helper function to prepare request from contract and arguments
function prepareRequest(api, key, args, baseUrl) {
    const contract = api[key];
    const contractKey = key;
    const method = extractMethodFromKey(contractKey);
    const pathTemplate = extractPathFromKey(contractKey);
    // Extract path parameters and input based on whether path params exist
    const pathParamNames = extractPathParams(pathTemplate);
    const hasPathParams = pathParamNames.length > 0;
    let pathParams = {};
    let input;
    if (hasPathParams) {
        if (args.length === 1) {
            // If path params exist and only one arg, it's pathParams, input defaults to {}
            pathParams = args[0];
            input = {};
        }
        else {
            // If path params exist and two args, it's [pathParams, input]
            pathParams = args[0];
            input = args[1];
        }
    }
    else {
        if (args.length === 0) {
            // If no path params and no args, input defaults to {}
            input = {};
        }
        else {
            // If no path params and one arg, it's input
            input = args[0];
        }
    }
    if (contract.params) {
        const paramsResult = v.safeParse(v.object(contract.params), pathParams);
        if (!paramsResult.success) {
            console.error('[client] The request path parameters are invalid:', paramsResult.issues);
            return { result: 'invalid-input', error: paramsResult.issues };
        }
        pathParams = paramsResult.output;
    }
    // Validate input using the contract's input schema
    const inputResult = v.safeParse(contract.input, input);
    if (!inputResult.success) {
        console.error('[client] The request input is invalid:', inputResult.issues);
        return { result: 'invalid-input', error: inputResult.issues };
    }
    const validatedInput = inputResult.output;
    // Substitute path parameters in the URL
    let url = substitutePath(pathTemplate, pathParams);
    // Prepare request options
    const requestInit = {
        method: method,
        credentials: 'include', // Include cookies for session handling
    };
    if (method !== 'GET') {
        requestInit.headers = {
            'Content-Type': 'application/json',
        };
    }
    // Handle different request types
    if (method === 'GET') {
        // For GET requests, add input as query parameters
        const params = new URLSearchParams();
        Object.entries(validatedInput).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                params.append(key, String(value));
            }
        });
        const queryString = params.toString();
        if (queryString) {
            url += `?${queryString}`;
        }
    }
    else {
        // For POST/PUT/PATCH/DELETE requests, add input as body
        requestInit.body = JSON.stringify(validatedInput);
    }
    const fullUrl = baseUrl ? `${baseUrl}${url}` : url;
    return { result: 'success', contract, fullUrl, requestInit };
}
function headersToRecord(headers) {
    const record = {};
    headers.forEach((value, key) => {
        record[key] = value;
    });
    return record;
}
// Helper function to handle response processing
async function processResponse(response, contract, options) {
    // Informational only: a differing build id says a deploy happened, no more.
    const serverVersion = response.headers.get('X-Studoku-Version');
    if (serverVersion && options.buildId && serverVersion !== options.buildId) {
        options.onVersionChange?.(serverVersion);
    }
    // `!==` catches a rollback too; safe because blue/green serves one epoch.
    const rawEpoch = response.headers.get('X-Studoku-Schema-Epoch');
    if (rawEpoch !== null && rawEpoch.trim() !== '') {
        const serverEpoch = Number(rawEpoch);
        if (Number.isInteger(serverEpoch) && serverEpoch !== SCHEMA_EPOCH) {
            options.onVersionRequired?.();
        }
    }
    // Any 503 is maintenance; skip output validation, the body may not be JSON.
    if (response.status === 503) {
        options.onMaintenance?.();
        return {
            success: false,
            errorType: 'client',
            message: 'Studoku is temporarily unavailable for maintenance',
            details: { maintenance: true },
        };
    }
    try {
        // Parse response as JSON
        const responseData = await response.json();
        if (!response.ok) {
            // Try to parse as error response
            const errorResult = v.safeParse(ErrorResponse, responseData);
            if (errorResult.success) {
                return {
                    success: false,
                    errorType: 'http',
                    message: errorResult.output.message,
                    status: response.status,
                    body: errorResult.output,
                    headers: headersToRecord(response.headers),
                };
            }
            else {
                console.error('[client] Got an unexpected error response:', responseData);
                return {
                    success: false,
                    errorType: 'client',
                    message: 'An unexpected error occurred',
                    details: responseData,
                };
            }
        }
        // Validate output using the contract's output schema
        const outputResult = v.safeParse(contract.output, responseData);
        if (outputResult.success) {
            return {
                success: true,
                body: outputResult.output,
                headers: headersToRecord(response.headers),
            };
        }
        else {
            console.error('[client] The server returned an invalid response:', outputResult.issues);
            return {
                success: false,
                errorType: 'client',
                message: 'The server returned an invalid response',
                details: outputResult.issues,
            };
        }
    }
    catch (error) {
        if (error instanceof TypeError && error.message.includes('fetch')) {
            console.error('[client] Network error:', error);
            return {
                success: false,
                errorType: 'client',
                message: 'Network error: Unable to connect to server',
                details: error,
            };
        }
        return {
            success: false,
            errorType: 'client',
            message: error instanceof Error
                ? error.message
                : 'Unknown error occurred',
            details: error,
        };
    }
}
function client(api, options) {
    // Store cookies manually for Node.js compatibility
    const cookieJar = new Map();
    return async function (key, ...args) {
        const requestResult = prepareRequest(api, key, args, options.baseUrl);
        if (requestResult.result === 'invalid-input') {
            return {
                success: false,
                errorType: 'client',
                message: 'The request input is invalid',
                details: requestResult.error,
            };
        }
        const { contract, fullUrl, requestInit } = requestResult;
        if (contract.requiresProofOfWork) {
            const proofOfWorkToken = await options.solveProofOfWork();
            if (proofOfWorkToken) {
                requestInit.headers = {
                    ...requestInit.headers,
                    Authorization: `ProofOfWork ${proofOfWorkToken}`,
                };
            }
        }
        // Add stored cookies to request
        if (cookieJar.size > 0) {
            const cookieString = Array.from(cookieJar.entries())
                .map(([name, value]) => `${name}=${value}`)
                .join('; ');
            requestInit.headers = {
                ...requestInit.headers,
                Cookie: cookieString,
            };
        }
        const response = await fetch(fullUrl, requestInit);
        // Store cookies from response
        const setCookieHeaders = response.headers.get('set-cookie');
        if (setCookieHeaders) {
            // Parse and store cookies (simple implementation)
            const cookies = setCookieHeaders
                .split(',')
                .map((cookie) => cookie.trim());
            for (const cookie of cookies) {
                const [nameValue] = cookie.split(';');
                const [name, value] = nameValue.split('=');
                if (name && value) {
                    cookieJar.set(name.trim(), value.trim());
                }
            }
        }
        return processResponse(response, contract, options);
    };
}
//# sourceMappingURL=client.js.map